import { gql } from '@apollo/client';
import React, { useCallback, useEffect, useMemo } from 'react';
import useContacts, {
	Contact,
	Contacts,
	NewContact,
} from '../../hooks/useContacts';

export const contactDiffers = (
	contact1: Contact | NewContact | undefined | null,
	contact2: Contact | undefined,
	compareIds = true
): boolean =>
	compareIds
		? !!(
			contact1 &&
				( ( 'id' in contact1 && contact1.id !== contact2?.id ) ||
					contactDiffers( contact1, contact2, false ) )
		  )
		: !!(
			contact1 &&
				( contact2?.firstName !== contact1.firstName ||
					contact2?.lastName !== contact1.lastName ||
					contact2?.customer?.email !== contact1.customer?.email ||
					contact2?.companyName !== contact1.companyName )
		  );

export const contactsContextContactFragment = gql`
	fragment contactsContextContact on Contact {
		id
		status
		firstName
		lastName
		companyName
		customer {
			id
			email
		}
	}
`;

export type ContactsContextProps = {
	loading: boolean;
	set: ( contact: Contact | NewContact | undefined, index?: number ) => boolean;
	remove: ( index: number ) => void;
	contacts: Contacts | undefined;
	organizationContacts: Contacts | undefined;
	organizationContactsReady: boolean;
	commit: () => Promise<Contacts | false>;
	filterOrganizationContacts: ( text?: string ) => Contacts | undefined;
	validate: ( force?: boolean ) => boolean | undefined;
	setValidationCallback: (
		callback?: ( force?: boolean ) => boolean,
		index?: number
	) => void;
};

type ContactsContextProviderProps = {
	organizationId?: string;
	throwErrors?: boolean;
	cacheKey?: string;
	children?: React.ReactNode;
};

export const ContactsContext = React.createContext<ContactsContextProps | null>(
	null
);

const ContactsContextProvider: React.FC<ContactsContextProviderProps> = ( {
	organizationId,
	throwErrors,
	cacheKey,
	children,
} ) => {
	const {
		loading,
		contacts: organizationContacts,
		isReady: organizationContactsReady,
		getContact,
		filterContacts: hookFilterContacts,
	} = useContacts( organizationId, throwErrors );
	// The contacts we want to consider for "this document"
	const [ contacts, setContacts ] = React.useState<Contacts | undefined>();

	// if the cacheKey changes, reset the contacts
	useEffect( () => {
		setContacts( undefined );
	}, [ cacheKey ] );

	const validationRef = React.useRef<
	Parameters<ContactsContextProps['setValidationCallback']>[0][]
	>( [] );
	const validate = useCallback(
		( force?: boolean ) =>
			validationRef.current.reduce(
				( acc, callback ) => ( callback ? ( acc = acc && callback( force ) ) : acc ),
				true
			),
		[]
	);

	// We can use this here and elsewhere to set the contacts we want to consider for "this document"
	const set: ContactsContextProps['set'] = useCallback( ( contact, index = 0 ) => {
		if ( typeof index === 'number' && index >= 0 ) {
			// A sneaky way to replace one item in an array:
			// https://stackoverflow.com/questions/45673783/replace-array-entry-with-spread-syntax-in-one-line-of-code#answer-45673826
			// Also, avoid setting the same contact over and over
			setContacts( ( prevState ) =>
				contact
					? contactDiffers( contact, prevState?.[ index ] )
						? Object.assign( [], prevState, { [ index ]: contact } )
						: prevState
					: prevState?.filter( ( _, stateIndex ) => stateIndex !== index )
			);
			return true;
		}
		return false;
	}, [] );
	// So we can delete a contact - probably only use to delete the end of the list
	const remove = useCallback(
		( index: number ) =>
			setContacts( ( prevState ) => prevState?.filter( ( _, i ) => i !== index ) ),
		[]
	);
	// Commit your changes
	const commit = useCallback( async () => {
		// optionally run validation
		const validation = validate();
		if ( typeof validation === 'boolean' && !validation ) {
			// abort the process on failure
			return false;
		}
		if ( !contacts || contacts.length === 0 ) {
			// this isn't a failure, simply no contacts have been selected
			return [];
		}
		const data: Contacts = [];
		for ( let index = 0; index < contacts.length; index++ ) {
			const contact = contacts[ index ];
			// A contact with no id and no email has no identity to commit — the user
			// touched the form inputs but didn't actually intend to add a signer
			if ( !contact.id && !contact.customer.email ) {
				continue;
			}
			const newContact = await getContact( contact );
			if ( newContact ) {
				set( newContact, index );
				data.push( newContact );
			} else {
				// we'll end up here in cases like were we try to create a contact with our own organization
				return newContact;
			}
		}
		return data;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contacts,
		getContact,
		validate
	] );
	// Filter, excluding any already-selected contacts
	const filterContacts = useCallback(
		( text?: string ) =>
			hookFilterContacts( text )?.filter(
				( c1 ) => !contacts?.some( ( c2 ) => c2.id === c1.id )
			),
		[ contacts, hookFilterContacts ]
	);
	const value: ContactsContextProps = useMemo(
		() => ( {
			loading,
			set,
			remove,
			filterOrganizationContacts: filterContacts,
			contacts,
			organizationContacts,
			organizationContactsReady,
			commit,
			validate,
			setValidationCallback: ( callback, index ) =>
				typeof index === 'number'
					? ( validationRef.current[ index ] = callback )
					: undefined,
		} ),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			commit,
			contacts,
			filterContacts,
			loading,
			organizationContacts,
			organizationContactsReady,
			remove,
		]
	);
	return (
		<ContactsContext.Provider value={ value }>
			{ children }
		</ContactsContext.Provider>
	);
};

export default ContactsContextProvider;
